import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInYears,
  format,
  formatDistanceToNow,
  isBefore,
  isValid,
  max,
  min,
  parse,
  parseISO,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { toZonedTime } from "date-fns-tz";

const handleStopPropagation = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const formatDate = (date, formatStr = "yyyy MMM dd") => {
  try {
    // Return "-" if the date is falsy (e.g., null, undefined, or empty)
    if (!date) {
      return "-";
    }
    // Parse the date string into a Date object
    const parsedDate = parseISO(date);

    // Check if the parsed date is valid
    if (isValid(parsedDate)) {
      return format(parsedDate, formatStr);
    }
    return "-";
  } catch (error) {
    console.error("Error formatting date:", error);
    return "-";
  }
};
const PLAN_TYPE_LABELS = {
  monthly: "Month",
  yearly: "Year",
};
const formatPlanType = (planType) => PLAN_TYPE_LABELS[planType] || planType;


const formatTime = (time) => {
  if (!time || typeof time !== "string") return ""; // Ensure `time` is a valid string

  try {
    // Parse the time string (e.g., "13:00") into a date object
    const parsedTime = parse(time, "HH:mm", new Date());

    // Check if the parsed time is valid
    if (isNaN(parsedTime)) {
      console.error("Invalid time value:", time);
      return ""; // Return an empty string for invalid time
    }

    // Format the time to 12-hour format with AM/PM
    return format(parsedTime, "h:mm a");
  } catch (error) {
    console.error("Error formatting time:", error);
    return ""; // Return an empty string on error
  }
};

const getTimeDifference = (start, end) => {
  try {
    if (!start || !end) return "-";

    // Parse time strings correctly (12-hour format with AM/PM)
    const startTime = parse(start, "hh:mm a", new Date());
    const endTime = parse(end, "hh:mm a", new Date());

    // Validate if parsed times are correct
    if (!isValid(startTime) || !isValid(endTime)) {
      return "-";
    }

    // Get the difference in minutes
    const diffMinutes = differenceInMinutes(endTime, startTime);

    // Convert minutes to hours and round to 1 decimal place
    return parseFloat((diffMinutes / 60).toFixed(1));
  } catch (error) {
    return "-";
  }
};

const getRemainingDaysForWorkshop = (selectedDateAndTimes) => {
  if (!selectedDateAndTimes || selectedDateAndTimes.length === 0)
    return "No Date Available";

  // Get the first date from the array
  const firstDate = selectedDateAndTimes[0]?.startTime?.date;

  if (!firstDate) return "Invalid Date";

  // Parse the date
  const parsedDate = parseISO(firstDate);

  // Validate the parsed date
  if (!isValid(parsedDate)) return "Invalid Date";

  // Calculate the difference in days
  const remainingDays = differenceInDays(parsedDate, new Date());

  return remainingDays > 0
    ? `${remainingDays} Day${remainingDays > 1 ? "s" : ""} Left!`
    : "Expired!";
};

const convertTo24HourFormat = (time) => {
  let { hours, minutes, period } = time;
  let hours24 = period === "PM" && hours !== 12 ? hours + 12 : hours;
  hours24 = period === "AM" && hours === 12 ? 0 : hours24;
  return `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}`;
};

function getMinMaxDate(dates = null) {
  if (!dates || dates.length === 0) return {};

  // Filter out null, undefined, and invalid dates
  const validDates = dates.filter((d) => d instanceof Date && isValid(d));

  if (validDates.length === 0) return {};

  if (validDates.length === 1) {
    const formattedDate = format(validDates[0], "yyyy-MM-dd");
    return {
      min: formattedDate,
      max: formattedDate,
    };
  }

  const minDate = min(validDates);
  const maxDate = max(validDates);

  return {
    min: format(minDate, "yyyy-MM-dd"),
    max: format(maxDate, "yyyy-MM-dd"),
  };
}

const getFormatDates = (dates) => {
  if (dates && dates.length === 2) {
    return {
      min: format(dates[0], "yyyy-MM-dd"),
      max: format(dates[1], "yyyy-MM-dd"),
    };
  }
  return {};
};

const getTimePart = (time, type) => {
  if (!time) return "";
  const parsed = parse(time, "HH:mm", new Date());
  return format(parsed, type);
};

const formatFilterOptions = (list, nameKey = "name", valueKey = "_id") => {
  return (
    list?.map((item) => ({
      name: item[nameKey] || "-",
      value: item[valueKey],
    })) || []
  );
};

const convertTo12HourObject = (time) => {
  if (!time) return { hours: 12, minutes: 0, period: "AM" };

  try {
    const parsed = parse(time, "HH:mm", new Date());

    return {
      hours: Number(format(parsed, "h")), // 12-hour format (no leading zero)
      minutes: parsed.getMinutes(),
      period: format(parsed, "a"), // AM / PM
    };
  } catch {
    return { hours: 12, minutes: 0, period: "AM" }; // fallback
  }
};

const getKeywordValue = (actionType, keyword) => {
  return ["RESET", "RESET_KEYWORD"].includes(actionType) ? "" : keyword || "";
};

const getRelativeTimeFromNow = (dateString, timeZone = "Asia/Riyadh") => {
  const date = new Date(dateString);

  if (isNaN(date.getTime()) || !timeZone) {
    return "-";
  }

  const zonedDate = toZonedTime(date, timeZone);
  const nowZoned = toZonedTime(new Date(), timeZone);
  const hoursDiff = differenceInHours(nowZoned, zonedDate);

  return hoursDiff < 24
    ? formatDistanceToNow(zonedDate, { addSuffix: true })
    : format(zonedDate, "MMM dd, yyyy");
};

const formatDateTime = (date) => {
  if (!date || !isValid(date)) return "";
  return format(date, "yyyy MMM dd, hh:mmaaa").toLowerCase();
};

const convertToUTCDateTimeString = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }

  // Format directly in UTC timezone
  return formatInTimeZone(date, "UTC", "yyyy-MM-dd'T'HH:mm:ss'Z'");
};

const getAgeFromDOB = (dobString) => {
  if (!dobString) return 0;

  try {
    let dob;

    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dobString)) {
      // yyyy-MM-dd
      dob = parse(dobString, "yyyy-MM-dd", new Date());
    } else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dobString)) {
      // d-M-yyyy or dd-MM-yyyy
      dob = parse(dobString, "d-M-yyyy", new Date());
    } else {
      console.error("Unsupported date format:", dobString);
      return 0;
    }

    const age = differenceInYears(new Date(), dob);
    return age >= 0 ? age : 0;
  } catch (error) {
    console.error("Failed to parse DOB:", dobString, error);
    return 0;
  }
};

const isExpired = (expiresAt) => {
  // Parse the input date string into a Date object
  const expiryDate = parse(expiresAt, "yyyy/M/d", new Date());

  // Get the current date
  const now = new Date();

  // Check if the current date is after the expiry date
  return isBefore(expiryDate, now);
};

const formatBookingDateAndTime = (bookingDate) => {
  if (!bookingDate) return "";
  return formatInTimeZone(
    bookingDate,
    "UTC",
    "yyyy MMM dd, hh:mmaaa"
  ).toLowerCase();
};

const formatToAmPm = (timeStr) => {
  if (!timeStr) return "";
  try {
    const parsedTime = parse(timeStr, "HH:mm", new Date());
    return format(parsedTime, "hh:mm a");
  } catch (error) {
    console.error("Invalid time string:", timeStr);
    return "";
  }
};

const formatToCustomDateTime = (date) => {
  const parsedDate = typeof date === "string" ? parseISO(date) : date;
  if (!parsedDate || !isValid(parsedDate)) return "";
  return format(parsedDate, "dd MMMM yyyy – h:mm a");
};

const toNumber = (value) => Number(value) || 0;
const CurrencySymbol = ({ color, width = "31", height = "36" }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 31 36"
      fill="none"
      style={{ marginRight: 3 }}
    >
      <g clip-path="url(#clip0_4692_40844)">
        <path
          d="M19.2933 31.4807C18.7401 32.7198 18.3745 34.0645 18.2344 35.4746L29.9409 32.9607C30.4941 31.7219 30.8595 30.3769 30.9999 28.9668L19.2933 31.4807Z"
          fill={color || "#31353B"}
        />
        <path
          d="M29.9401 25.4294C30.4933 24.1906 30.8589 22.8456 30.999 21.4355L21.88 23.3947V19.6284L29.9398 17.8981C30.493 16.6593 30.8587 15.3143 30.9987 13.9042L21.8797 15.8617V2.31683C20.4824 3.10938 19.2414 4.16435 18.2327 5.40875V16.6451L14.5857 17.4282V0.474609C13.1884 1.26688 11.9474 2.32212 10.9387 3.56652V18.211L2.77847 19.9627C2.22529 21.2015 1.85934 22.5465 1.71898 23.9566L10.9387 21.9773V26.7204L1.05797 28.8414C0.504779 30.0803 0.139113 31.4252 -0.000976562 32.8354L10.3414 30.6151C11.1833 30.4382 11.9069 29.9354 12.3773 29.2434L14.2741 26.4028V26.4022C14.471 26.1083 14.5857 25.754 14.5857 25.3723V21.1943L18.2327 20.4112V27.9439L29.9398 25.4289L29.9401 25.4294Z"
          fill={color || "#31353B"}
        />
      </g>
      <defs>
        <clipPath id="clip0_4692_40844">
          <rect
            width="31"
            height="35"
            fill="white"
            transform="translate(0 0.474609)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};


function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}





export {
  handleStopPropagation,
  formatDate,
  formatTime,
  getTimeDifference,
  getRemainingDaysForWorkshop,
  convertTo24HourFormat,
  getMinMaxDate,
  getFormatDates,
  formatFilterOptions,
  getKeywordValue,
  getTimePart,
  convertTo12HourObject,
  getRelativeTimeFromNow,
  formatDateTime,
  convertToUTCDateTimeString,
  getAgeFromDOB,
  isExpired,
  formatBookingDateAndTime,
  formatToAmPm,
  formatToCustomDateTime,
  CurrencySymbol,
  toNumber,
  formatPlanType,
  capitalizeFirstLetter
};
